import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowMove } from '../../../svg/arrows-move-svgrepo-com.svg';
import { ReactComponent as ArrowRotate } from '../../../svg/arrow-clockwise-svgrepo-com.svg';
import { ReactComponent as SymmetryVertical } from '../../../svg/symmetry-vertical-svgrepo-com.svg';
import { ReactComponent as Copy } from '../../../svg/grid-svgrepo-com.svg';
import { ReactComponent as Scale } from '../../../svg/rulers-svgrepo-com.svg';
import { ReactComponent as LayerForward } from '../../../svg/layer-forward-svgrepo-com.svg';
import { ReactComponent as Delete } from '../../../svg/delete-svgrepo-com.svg';

const ContextualToolPanel = ({
    onExecuteOperation,
    onArrange,
    onTransform,
    position,
    visible,
    selectedPolygonCount,
    selectionCount,
    transformMode,
    insertVertexMode,
    onToggleInsertVertex,
    canInsertVertex
}) => {
    const { t } = useTranslation();
    const panelRef = useRef(null);
    const [adjustedPosition, setAdjustedPosition] = useState({ x: position?.x || 0, y: position?.y || 0 });

    useEffect(() => {
        if (panelRef.current && position) {
            const panelRect = panelRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Calculate adjusted position to keep panel within viewport
            const adjustedX = Math.min(position.x, viewportWidth - panelRect.width - 10);
            const adjustedY = Math.min(position.y, viewportHeight - panelRect.height - 10);

            setAdjustedPosition({
                x: Math.max(10, adjustedX),
                y: Math.max(10, adjustedY)
            });
        }
    }, [position]);

    const showBooleanOps = selectedPolygonCount >= 2;

    useEffect(() => {
        if (!visible) return;
        const handleKeyDown = (e) => {
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            const tag = e.target.tagName.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
            const key = e.key.toLowerCase();
            if (key === 'v') { e.preventDefault(); onTransform('move'); }
            else if (key === 'c') { e.preventDefault(); onTransform('copy'); }
            else if (key === 'r') { e.preventDefault(); onTransform('rotate'); }
            else if (key === 'f') { e.preventDefault(); onTransform('symmetry'); }
            else if (key === 'z') { e.preventDefault(); onTransform('scale'); }
            else if (key === 'i' && canInsertVertex) { e.preventDefault(); onToggleInsertVertex(); }
            else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); onTransform('delete'); }
            else if (key === ']') { e.preventDefault(); onArrange('front'); }
            else if (key === '[') { e.preventDefault(); onArrange('back'); }
            else if (key === 'u' && showBooleanOps) { e.preventDefault(); onExecuteOperation('union'); }
            else if (key === 'x' && showBooleanOps) { e.preventDefault(); onExecuteOperation('difference'); }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [visible, onTransform, onArrange, onExecuteOperation, showBooleanOps, onToggleInsertVertex, canInsertVertex]);

    if (!visible || !position || selectionCount === 0) {
        return <></>;
    }

    return (
        <div
            ref={panelRef}
            className="contextual-tool-panel"
            style={{
                // left: `${adjustedPosition.x}px`,
                // top: `${adjustedPosition.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {/* Arrangement Controls - Always show if at least one item is selected */}
            <button onClick={() => onTransform('move')} className={transformMode === 'move' ? 'transform-active' : ''} data-tt={`${t('replan.contextualToolPanel.move_desc')}\n${t('replan.contextualToolPanel.move_name')}\n${t('common.shortcut', { key: 'V', defaultValue: 'V' })}`}>
                <ArrowMove className="tool-icon" style={{ height: '0.9rem', width: '0.9rem' }} />
            </button>
            <button onClick={() => onTransform('copy')} className={transformMode === 'copy' ? 'transform-active' : ''} data-tt={`${t('replan.contextualToolPanel.copy_desc')}\n${t('replan.contextualToolPanel.copy_name')}\n${t('common.shortcut', { key: 'C', defaultValue: 'C' })}`}>
                <Copy className="tool-icon" style={{ height: '0.9rem', width: '0.9rem' }} />
            </button>
            <button onClick={() => onTransform('rotate')} className={transformMode === 'rotate' ? 'transform-active' : ''} data-tt={`${t('replan.contextualToolPanel.rotate_desc')}\n${t('replan.contextualToolPanel.rotate_name')}\n${t('common.shortcut', { key: 'R', defaultValue: 'R' })}`}>
                <ArrowRotate className="tool-icon" style={{ height: '0.9rem', width: '0.9rem' }} />
            </button>
            <button onClick={() => onTransform('symmetry')} className={transformMode === 'symmetry' ? 'transform-active' : ''} data-tt={`${t('replan.contextualToolPanel.symmetry_desc')}\n${t('replan.contextualToolPanel.symmetry_name')}\n${t('common.shortcut', { key: 'F', defaultValue: 'F' })}`}>
                <SymmetryVertical className="tool-icon" style={{ height: '0.9rem', width: '0.9rem' }} />
            </button>
            <button onClick={() => onTransform('scale')} className={transformMode === 'scale' ? 'transform-active' : ''} data-tt={`${t('replan.contextualToolPanel.scale_desc')}\n${t('replan.contextualToolPanel.scale_name')}\n${t('common.shortcut', { key: 'Z', defaultValue: 'Z' })}`}>
                <Scale className="tool-icon" style={{ height: '0.9rem', width: '0.9rem' }} />
            </button>

            <button onClick={() => onTransform('delete')} data-tt={`${t('replan.contextualToolPanel.delete_desc')}\n${t('replan.contextualToolPanel.delete_name')}\n${t('common.shortcut', { key: 'Del', defaultValue: 'Del' })}`}>
                <Delete className="tool-icon" style={{ height: '0.9rem', width: '0.9rem' }} />
            </button>

            <button onClick={() => onArrange('front')} data-tt={`${t('replan.contextualToolPanel.bring_front_desc')}\n${t('replan.contextualToolPanel.bring_front_name')}\n${t('common.shortcut', { key: ']', defaultValue: ']' })}`}>
                <LayerForward className="tool-icon" style={{ height: '0.9rem', width: '0.9rem' }} />
            </button>
            <button onClick={() => onArrange('back')} data-tt={`${t('replan.contextualToolPanel.send_back_desc')}\n${t('replan.contextualToolPanel.send_back_name')}\n${t('common.shortcut', { key: '[', defaultValue: '[' })}`}>
                <LayerForward className="tool-icon" style={{ transform: 'rotate(180deg)', height: '0.9rem', width: '0.9rem' }} />
            </button>

            {canInsertVertex && (
                <> <div className="horizontal-separator"></div>
                <button onClick={() => onToggleInsertVertex()} className={insertVertexMode ? 'transform-active' : ''} data-tt={`${t('replan.contextualToolPanel.insertVertex_desc')}\n${t('replan.contextualToolPanel.insertVertex_name')}\n${t('common.shortcut', { key: 'I', defaultValue: 'I' })}`}>
                    <strong style={{ fontWeight: 700 }}>＋</strong>
                </button></>
            )}

            {/* Boolean Operations - Show only when applicable */}
            {showBooleanOps && (
                <>
                    <div className="horizontal-separator"></div>
                    <button onClick={() => onExecuteOperation('union')} data-tt={`${t('replan.contextualToolPanel.union_desc')}\n${t('replan.contextualToolPanel.union_name')}\n${t('common.shortcut', { key: 'U', defaultValue: 'U' })}`}>
                        <strong >∪</strong>
                    </button>
                    {/* <button onClick={() => onExecuteOperation('subtract')} data-tt={`${t('replan.contextualToolPanel.subtract_desc')}\n${t('replan.contextualToolPanel.subtract_name')}\n${t('common.shortcut', { key: 'S', defaultValue: 'S' })}`}>
                        <strong>⊖</strong>
                    </button>
                    <button onClick={() => onExecuteOperation('intersect')} data-tt={`${t('replan.contextualToolPanel.intersect_desc')}\n${t('replan.contextualToolPanel.intersect_name')}\n${t('common.shortcut', { key: 'I', defaultValue: 'I' })}`}>
                        <strong>∩</strong>
                    </button> */}
                    <button onClick={() => onExecuteOperation('difference')} data-tt={`${t('replan.contextualToolPanel.difference_desc')}\n${t('replan.contextualToolPanel.difference_name')}\n${t('common.shortcut', { key: 'X', defaultValue: 'X' })}`}>
                        <strong>⊕</strong>
                    </button>
                </>
            )}
        </div>
    );
};

export default ContextualToolPanel;